import React, { useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch, useOnboardingRoute } from '@/hooks';
import { useGetBrandConfigQuery } from '@/redux/internal-services/brandApi';
import { useGetMeQuery, usersApi } from '@/redux/internal-services/usersApi';
import { setBrand, DEFAULT_BRAND } from '@/redux/slices/brandSlice';
import { applyTheme } from '@/utils/theme';
import { setColorMode, DEFAULT_COLOR_MODE, type ColorMode } from '@/redux/slices/colorModeSlice';
import { setRoleNames } from '@/redux/slices/sessionStateSlice';
import { socketService } from '@/services/SocketService';
import { pushNotificationService } from '@/services/PushNotificationService';
import { notificationsApi, useRegisterDeviceMutation, useUnregisterDeviceMutation } from '@/redux/internal-services/notificationsApi';

import '@/redux/internal-services/workspaceApiExtensions';
import { workspaceApi } from '@/redux/internal-services/workspaceApi';
import { workflowEngineApi } from '@/redux/internal-services/workflowEngineApi';
import BaseLayout from '@/layouts/BaseLayout';
import DashboardLayout from '@/layouts/DashboardLayout';
import AuthGuard from '@/components/Auth/AuthGuard';
import ErrorBoundary from '@/components/ErrorBoundary';
import Toaster from '@/components/common/Toaster';

// Auth pages
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import VerifyEmailPage from '@/pages/VerifyEmailPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import InvitationAcceptPage from '@/pages/InvitationAcceptPage';
import TenantSuspendedPage from '@/pages/TenantSuspendedPage';
import AccountSuspendedPage from '@/pages/AccountSuspendedPage';

// Onboarding pages
import PlanSelectionPage from '@/pages/onboarding/PlanSelectionPage';
import SetupWizardPage from '@/pages/onboarding/SetupWizardPage';
import UserOnboardingPage from '@/pages/onboarding/UserOnboardingPage';

// App pages
import ReportsPage from '@/pages/reports/ReportsPage';
import WeatherPage from '@/pages/weather/WeatherPage';
import MarketingPage from '@/pages/marketing/MarketingPage';

// Settings & admin pages
import TenantSettingsPage from '@/pages/settings/TenantSettingsPage';
import ProfilePage from '@/pages/profile/ProfilePage';
import TenantLifecyclePage from '@/pages/admin/TenantLifecyclePage';
import RolesPage from '@/pages/admin/RolesPage';
import EmailTemplatesPage from '@/pages/admin/EmailTemplatesPage';

// Platform admin pages
import TenantsPage from '@/pages/platform/TenantsPage';
import TenantDetailPage from '@/pages/platform/TenantDetailPage';
import UserDetailPage from '@/pages/platform/UserDetailPage';
import PlansPage from '@/pages/platform/PlansPage';
import ConfigurationsPage from '@/pages/platform/ConfigurationsPage';
import SamPromptsPage from '@/pages/platform/SamPromptsPage';
import RulebookPage from '@/pages/platform/RulebookPage';
import SessionLogsPage from '@/pages/platform/SessionLogsPage';
import LogsPage from '@/pages/platform/LogsPage';
import ChatMonitorPage from '@/pages/platform/ChatMonitorPage';
import UsagePage from '@/pages/platform/UsagePage';
import AiBillingPage from '@/pages/platform/AiBillingPage';
import PricingPage from '@/pages/platform/PricingPage';
import ToolErrorsPage from '@/pages/platform/ToolErrorsPage';
import AgentsPage from '@/pages/platform/AgentsPage';
import AgentConfigPage from '@/pages/platform/AgentConfigPage';
import ChatPage from '@/pages/chat/ChatPage';


import PlatformUsersPage from '@/pages/platform/UsersPage';
import NotificationsAdminPage from '@/pages/platform/NotificationsAdminPage';
import QueueManagementPage from '@/pages/platform/QueueManagementPage';
import SchedulerEventsPage from '@/pages/platform/SchedulerEventsPage';
import WorkflowsPage from '@/pages/platform/WorkflowsPage';
import WorkflowDefinitionPage from '@/pages/platform/WorkflowDefinitionPage';
import WorkflowEditorPage from '@/pages/platform/WorkflowEditorPage';
import WorkflowCreateWizardPage from '@/pages/platform/WorkflowCreateWizardPage';
import WorkflowGovernancePage from '@/pages/platform/WorkflowGovernancePage';
import WorkflowFlowchartPage from '@/pages/platform/WorkflowFlowchartPage';

const ColorModeLoader: React.FC = () => {
  const dispatch = useAppDispatch();
  const mode: ColorMode = useAppSelector((s) => s.colorMode?.mode ?? DEFAULT_COLOR_MODE);
  const isAuthenticated = useAppSelector((s) => s.sessionState.isAuthenticated);

  // The sign-in and register pages sit on the always-dark brand background, but their text colours follow the saved mode. A mode
  // left in localStorage by the previous user (Light, say, when the session simply expired) would draw dark text on that dark
  // background. A signed-out browser therefore always uses the default; ThemeSyncLoader applies the user's own choice after sign-in.
  useEffect(() => {
    if (!isAuthenticated && mode !== DEFAULT_COLOR_MODE) dispatch(setColorMode(DEFAULT_COLOR_MODE));
  }, [isAuthenticated, mode, dispatch]);

  useEffect(() => {
    if (mode === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const apply = (prefersDark: boolean) => {
        document.documentElement.classList.toggle('dark', prefersDark);
      };
      apply(mq.matches);
      const handler = (e: MediaQueryListEvent) => apply(e.matches);
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    } else {
      document.documentElement.classList.toggle('dark', mode === 'dark');
    }
  }, [mode]);

  return null;
};

const ThemeSyncLoader: React.FC = () => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((s) => s.sessionState.isAuthenticated);
  const storedRoleNames = useAppSelector((s) => s.sessionState.roleNames);
  const { data: profile } = useGetMeQuery(undefined, { skip: !isAuthenticated });

  useEffect(() => {
    // Apply the loaded user's saved color mode. A user with NO saved preference
    // (theme_mode === null) must fall back to the default (dark, the RoofVox brand direction) — not silently keep whatever mode the
    // previous user left behind in the localStorage-persisted colorMode slice. The old
    // `if (profile?.theme_mode)` guard skipped the dispatch for such users, so the previous
    // user's theme persisted across logout → login.
    if (profile) {
      dispatch(setColorMode(profile.theme_mode ?? DEFAULT_COLOR_MODE));
    }
  }, [profile, dispatch]);

  useEffect(() => {
    // sessionState.roleNames is only ever populated at login time (setSession) and is
    // persisted across sessions via redux-persist. A session started before role_names
    // existed on the login payload — or before a role change — would otherwise be stuck
    // showing stale/empty roles until the user manually logs out and back in. /me already
    // resolves the current tenant membership's role names on every load, so use it here to
    // self-heal the stored value instead of requiring a re-login.
    if (!profile) return;
    const fresh = profile.role_names ?? [];
    const same =
      fresh.length === storedRoleNames.length && fresh.every((r, i) => r === storedRoleNames[i]);
    if (!same) {
      dispatch(setRoleNames(fresh));
    }
  }, [profile, storedRoleNames, dispatch]);

  return null;
};

const ThemeLoader: React.FC = () => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((s) => s.sessionState.isAuthenticated);
  const { data: brandConfig } = useGetBrandConfigQuery(undefined, { skip: !isAuthenticated });

  // The browser tab always shows the SmartContractor product name + icon (set statically in
  // index.html). The tenant's company name and logo are shown only in the in-app header (Sidebar),
  // never in the browser tab.

  useEffect(() => {
    // Brand colors are CSS variables on <html> that survive SPA navigation and are only cleared by
    // a full page reload — so they MUST be reset explicitly. When signed out (brandConfig query is
    // skipped) or on a tenant with no custom colors, fall back to the default palette; otherwise a
    // previous tenant's brand lingers on public/onboarding pages (login, verify-email OTP, plans).
    const bc = isAuthenticated ? brandConfig : undefined;
    const primaryColor = bc?.primary_color || DEFAULT_BRAND.primaryColor;
    const accentColor = bc?.accent_color || DEFAULT_BRAND.accentColor;
    const successColor = bc?.success_color || DEFAULT_BRAND.successColor;
    // Internal input (system-layer default RoofVox Brand Navy; a tenant-layer override is honoured if one exists).
    const baseColor = bc?.base_color || DEFAULT_BRAND.baseColor;
    applyTheme({
      primary_color: primaryColor,
      accent_color: accentColor,
      success_color: successColor,
      base_color: baseColor,
    });
    dispatch(setBrand({
      primaryColor,
      accentColor,
      successColor,
      baseColor,
      logoLightUrl: bc?.logo_light_url || '',
      logoDarkUrl: bc?.logo_dark_url || '',
      faviconUrl: bc?.favicon_url || '',
    }));

    // Note: the tenant favicon is intentionally NOT applied to the browser tab. The tab keeps the
    // default SmartContractor icon (from index.html); the tenant logo appears only in the in-app
    // header. faviconUrl is still stored in brand state for Tenant Settings previews/uploads.
  }, [brandConfig, isAuthenticated, dispatch]);

  return null;
};

/**
 * Owns the Socket.IO connection lifecycle: connect once authenticated (so the realtime
 * gateway → Redis pub/sub → api-service chain is live for voice and other realtime features),
 * reconnect when the access token changes (refresh), and disconnect on logout.
 *
 * Also handles mobile recovery: when the tab comes back to foreground (visibilitychange) or the
 * device regains network (online), attempts a reconnect if the socket dropped while in background.
 * This covers the common "SAM offline after returning from background" pattern on iOS/Android.
 */
const SocketConnector: React.FC = () => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((s) => s.sessionState.isAuthenticated);
  const accessToken = useAppSelector((s) => s.sessionState.accessToken);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) {
      socketService.disconnect();
      return;
    }
    // Force a token refresh via a cheap authenticated request: baseQueryWithReauth refreshes
    // proactively, dispatches the new accessToken to Redux, and this effect re-runs with it.
    const refreshToken = () =>
      dispatch(usersApi.endpoints.getMe.initiate(undefined, { forceRefetch: true }));
    // Gateway rejected the handshake (token expired mid-session, e.g. a socket.io auto-reconnect
    // replaying the old token) — refresh instead of waiting for some unrelated API call.
    socketService.setAuthErrorHandler(refreshToken);
    // Stored token already expired — refresh first; connecting now would only be rejected.
    try {
      const { exp } = JSON.parse(atob(accessToken.split('.')[1]));
      if (Date.now() >= (exp - 30) * 1000) {
        refreshToken();
        return () => socketService.setAuthErrorHandler(null);
      }
    } catch { /* malformed JWT — attempt connect and let the server decide */ }
    socketService.connect(accessToken);
    return () => socketService.setAuthErrorHandler(null);
  }, [isAuthenticated, accessToken, dispatch]);

  // Background/network recovery: re-attempt connect when the page becomes visible or the
  // device reconnects. Idempotent — SocketService.connect() is a no-op if already connected.
  //
  // A tab left in the background for several minutes commonly outlives the access token's TTL.
  // The gateway then rejects the handshake with an auth error, but socket.io's websocket-only
  // transport doesn't reliably propagate that reason string to the client (it surfaces as the
  // generic "Connection refused by server"), so SocketService can't detect it's an auth failure
  // and just keeps retrying with the same dead token forever — the "Reconnecting to SAM" banner
  // never clears. Force a cheap authenticated request first: if the token expired,
  // baseQueryWithReauth's 401 handling refreshes it and dispatches the new token to Redux, which
  // the effect above picks up and reconnects with automatically. Only then attempt the socket
  // reconnect directly, for the case where the token was still valid and the socket merely
  // dropped (e.g. a network blip).
  useEffect(() => {
    if (!isAuthenticated || !accessToken) return;
    const token = accessToken;
    const tryReconnect = () => {
      if (socketService.isConnected) return;
      dispatch(usersApi.endpoints.getMe.initiate(undefined, { forceRefetch: true }));
      socketService.connect(token);
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') tryReconnect();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('online', tryReconnect);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('online', tryReconnect);
    };
  }, [isAuthenticated, accessToken, dispatch]);

  return null;
};

/**
 * Owns the push notification lifecycle: requests permission on auth, registers the FCM
 * token with the backend, and deregisters + tears down Firebase on logout.
 */
const PushConnector: React.FC = () => {
  const isAuthenticated = useAppSelector((s) => s.sessionState.isAuthenticated);
  const [registerDevice] = useRegisterDeviceMutation();
  const [unregisterDevice] = useUnregisterDeviceMutation();
  const deviceIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      if (deviceIdRef.current) {
        unregisterDevice(deviceIdRef.current).catch(() => {});
        deviceIdRef.current = null;
      }
      pushNotificationService.teardown().catch(() => {});
      return;
    }

    pushNotificationService.setup().then((token) => {
      if (!token) return;
      registerDevice({ device_token: token, platform: 'web', provider: 'fcm' })
        .unwrap()
        .then((device) => { deviceIdRef.current = device.id; })
        .catch(() => {});
    });
  }, [isAuthenticated]);

  return null;
};

/**
 * Listens for real-time `notification:new` Socket.IO events and invalidates the
 * inbox + counts RTK Query cache so all open notification UI refreshes automatically.
 */
const NotificationSocketListener: React.FC = () => {
  const isAuthenticated = useAppSelector((s) => s.sessionState.isAuthenticated);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!isAuthenticated) return;
    const handler = () => {
      dispatch(notificationsApi.util.invalidateTags(['InboxCounts', 'Inbox']));
    };
    socketService.on('notification:new', handler);
    return () => socketService.off('notification:new', handler);
  }, [isAuthenticated, dispatch]);

  return null;
};

/**
 * Shows a dismissible toast for FCM push messages received while the app is in the
 * foreground. `onMessage` (unlike the service worker's onBackgroundMessage) never
 * displays anything on its own — without this, a foreground push is silently dropped.
 */
const ForegroundPushToast: React.FC = () => {
  const isAuthenticated = useAppSelector((s) => s.sessionState.isAuthenticated);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [toast, setToast] = useState<{ title: string; body: string; actionRoute?: string } | null>(null);
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    const unsubscribe = pushNotificationService.onForegroundMessage((payload) => {
      dispatch(notificationsApi.util.invalidateTags(['InboxCounts', 'Inbox']));
      setToast({
        title: payload.title || 'RoofVox',
        body: payload.body || '',
        actionRoute: payload.data?.action_route,
      });
      if (dismissTimer.current) clearTimeout(dismissTimer.current);
      dismissTimer.current = setTimeout(() => setToast(null), 6000);
    });
    // Real-time in-app notifications (reminders etc.) arrive over Socket.IO, not FCM — surface
    // them with the same toast so an online user sees them instantly, not only as a badge bump.
    const socketHandler = (data: unknown) => {
      const n = (data ?? {}) as { title?: string; message?: string; action_route?: string | null };
      setToast({
        title: n.title || 'RoofVox',
        body: n.message || '',
        actionRoute: n.action_route || undefined,
      });
      if (dismissTimer.current) clearTimeout(dismissTimer.current);
      dismissTimer.current = setTimeout(() => setToast(null), 6000);
    };
    socketService.on('notification:new', socketHandler);
    return () => {
      unsubscribe();
      socketService.off('notification:new', socketHandler);
      if (dismissTimer.current) clearTimeout(dismissTimer.current);
    };
  }, [isAuthenticated, dispatch]);

  if (!toast) return null;

  const handleClick = () => {
    if (toast.actionRoute) navigate(toast.actionRoute);
    setToast(null);
  };

  return (
    <div
      role="alert"
      onClick={handleClick}
      className="fixed top-4 right-4 z-50 max-w-sm cursor-pointer rounded-xl border border-ui-border bg-ui-card px-4 py-3 shadow-lg transition-all"
    >
      <p className="text-sm font-medium text-ui-text">{toast.title}</p>
      {toast.body && <p className="mt-1 text-xs text-ui-text2">{toast.body}</p>}
    </div>
  );
};

/**
 * Listens for real-time task/meeting/communication/reminder Socket.IO events
 * and invalidates the corresponding RTK Query cache tags.
 */
const WorkspaceSocketListener: React.FC = () => {
  const isAuthenticated = useAppSelector((s) => s.sessionState.isAuthenticated);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!isAuthenticated) return;

    const invalidate = (...tags: string[]) => {
      dispatch(workspaceApi.util.invalidateTags(tags as never[]));
    };

    const taskHandler = () => invalidate('Task');
    const meetingHandler = () => invalidate('Meeting', 'CalendarEvent');
    const commHandler = () => invalidate('Communication');
    const reminderHandler = () => invalidate('Reminder');
    const engagementHandler = () => {
      const tags: Parameters<typeof workflowEngineApi.util.invalidateTags>[0] = [
        'EngagementWorkflowState', 'AvailableTransitions', 'PendingApprovals',
        'EngagementActivity', 'EngagementList', 'Engagement',
      ];
      dispatch(workflowEngineApi.util.invalidateTags(tags as never[]));
    };

    const taskEvents = ['task:created', 'task:updated', 'task:assigned', 'task:claimed', 'task:status_changed', 'task:follower_added'];
    const meetingEvents = ['meeting:created', 'meeting:updated', 'meeting:rescheduled', 'meeting:cancelled', 'meeting:note_added'];
    const commEvents = ['communication:sent', 'communication:failed', 'communication:received'];
    const reminderEvents = ['reminder:fired'];
    const engagementEvents = ['engagement:status_changed', 'engagement:approval_decided'];

    taskEvents.forEach((e) => socketService.on(e, taskHandler));
    meetingEvents.forEach((e) => socketService.on(e, meetingHandler));
    commEvents.forEach((e) => socketService.on(e, commHandler));
    reminderEvents.forEach((e) => socketService.on(e, reminderHandler));
    engagementEvents.forEach((e) => socketService.on(e, engagementHandler));

    return () => {
      taskEvents.forEach((e) => socketService.off(e, taskHandler));
      meetingEvents.forEach((e) => socketService.off(e, meetingHandler));
      commEvents.forEach((e) => socketService.off(e, commHandler));
      reminderEvents.forEach((e) => socketService.off(e, reminderHandler));
      engagementEvents.forEach((e) => socketService.off(e, engagementHandler));
    };
  }, [isAuthenticated, dispatch]);

  return null;
};

const ChatGuard: React.FC = () => {
  const roles = useAppSelector((s) => s.sessionState.roles);
  const isPlatformAdmin = roles.includes('platform_admin') || roles.includes('platform_support');
  if (isPlatformAdmin) return <Navigate to="/platform/tenants" replace />;
  return <ChatPage />;
};

const RootRedirect: React.FC = () => {
  // useOnboardingRoute owns the full sequence (pre-plan setup → plans → setup → settings),
  // so this just follows its target. Wait while it loads to avoid flashing the dashboard.
  const roles = useAppSelector((s) => s.sessionState.roles);
  const isPlatformAdmin = roles.includes('platform_admin') || roles.includes('platform_support');
  const { target: onboardingTarget, isLoading: onboardingLoading } = useOnboardingRoute();
  if (onboardingLoading) return (
    <div className="min-h-dvh flex items-center justify-center bg-ui-subtle">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-ui-border border-t-accent" />
    </div>
  );
  if (isPlatformAdmin) return <Navigate to="/platform/tenants" replace />;
  if (onboardingTarget) {
    return <Navigate to={onboardingTarget} replace />;
  }
  return <Navigate to="/chat" replace />;
};

const ApprovalRedirect: React.FC = () => {
  // Legacy deep link — approvals no longer have a standalone page (see comment below), but
  // notification emails/push were already sent with /workflow/approvals/:id links baked in.
  // Forward those straight page-loads into the workspace the same way NotificationItem.tsx
  // rewrites the in-app click.
  const { approvalId } = useParams<{ approvalId: string }>();
  return <Navigate to={`/chat?approval=${approvalId}`} replace />;
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
    <ColorModeLoader />
    <ThemeSyncLoader />
    <ThemeLoader />
    <SocketConnector />
    <PushConnector />
    <ForegroundPushToast />
    <Toaster />
    <NotificationSocketListener />
    <WorkspaceSocketListener />
    <Routes>
      {/* Public auth routes */}
      <Route element={<BaseLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/invite/:token" element={<InvitationAcceptPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/suspended/tenant" element={<TenantSuspendedPage />} />
        <Route path="/suspended/account" element={<AccountSuspendedPage />} />
      </Route>

      {/* Onboarding (no sidebar) */}
      <Route element={<BaseLayout />}>
        <Route
          path="/onboarding/plans"
          element={<AuthGuard element={<PlanSelectionPage />} requiredPermissions={['subscription:read']} />}
        />
        <Route
          path="/onboarding/setup"
          element={<AuthGuard element={<SetupWizardPage />} />}
        />
        <Route
          path="/onboarding/profile"
          element={<AuthGuard element={<UserOnboardingPage />} />}
        />
      </Route>

      {/* Authenticated app (with sidebar) */}
      <Route element={<AuthGuard element={<DashboardLayout />} />}>
        {/* SAM AI Chat — platform admins are redirected to /platform/tenants */}
        <Route path="/chat" element={<ChatGuard />} />

        {/* Legacy deep link from notification emails/push — see ApprovalRedirect above */}
        <Route path="/workflow/approvals/:approvalId" element={<ApprovalRedirect />} />

        {/* Tasks — no dedicated pages; task access is via SAM workspace cards only */}


        {/* Properties + Engagements + Workflow have no dedicated pages by design — SAM opens the
            Workflow/Engagements cards in the chat workspace (see WorkflowCard.tsx); the underlying
            tools are global (page_tools.py GLOBAL_TOOLS), reachable from any chat turn.
            Approvals are handled inline in the workspace NotificationsPanel — no standalone page.
            Notification action_route=/workflow/approvals/:id redirects to /?approval=<id> so
            ChatPage can open the ApprovalDetailPanel in the workspace instead. */}

        {/* Note Taker and Calendar live in the SAM chat workspace — no standalone pages. */}

        {/* Insights */}
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/weather" element={<WeatherPage />} />
        <Route path="/marketing" element={<MarketingPage />} />

        {/* Settings */}
        <Route
          path="/settings/tenant"
          element={<AuthGuard element={<TenantSettingsPage />} requiredPermissions={['tenant:read']} />}
        />
        <Route path="/profile" element={<AuthGuard element={<ProfilePage />} />} />

        {/* Platform admin */}
        <Route
          path="/admin/lifecycle"
          element={<AuthGuard element={<TenantLifecyclePage />} requiredPermissions={['tenant:lifecycle:write']} />}
        />
        <Route
          path="/admin/roles"
          element={<AuthGuard element={<RolesPage />} requiredPermissions={['role:read']} />}
        />
        <Route
          path="/admin/email-templates"
          element={<AuthGuard element={<EmailTemplatesPage />} requiredPermissions={['platform:admin']} />}
        />

        {/* Platform admin — cross-tenant management */}
        <Route
          path="/platform/tenants"
          element={<AuthGuard element={<TenantsPage />} requiredPermissions={['platform:admin']} />}
        />
        <Route
          path="/platform/tenants/:tenantId"
          element={<AuthGuard element={<TenantDetailPage />} requiredPermissions={['platform:admin']} />}
        />
        <Route
          path="/platform/users/:userId"
          element={<AuthGuard element={<UserDetailPage />} requiredPermissions={['platform:admin']} />}
        />
        <Route
          path="/platform/users"
          element={<AuthGuard element={<PlatformUsersPage />} requiredPermissions={['platform:admin']} />}
        />
        <Route
          path="/platform/plans"
          element={<AuthGuard element={<PlansPage />} requiredPermissions={['platform:admin']} />}
        />
        <Route
          path="/platform/configurations"
          element={<AuthGuard element={<ConfigurationsPage />} requiredPermissions={['platform:admin']} />}
        />
        <Route
          path="/platform/agents"
          element={<AuthGuard element={<AgentsPage />} requiredPermissions={['platform:admin']} />}
        />
        <Route
          path="/platform/agents/:agentId"
          element={<AuthGuard element={<AgentConfigPage />} requiredPermissions={['platform:admin']} />}
        />
        <Route
          path="/platform/sam-prompts"
          element={<AuthGuard element={<SamPromptsPage />} requiredPermissions={['platform:admin']} />}
        />
        <Route
          path="/platform/sam-rulebook"
          element={<AuthGuard element={<RulebookPage />} requiredPermissions={['platform:admin']} />}
        />
        <Route
          path="/platform/logs"
          element={<AuthGuard element={<LogsPage />} requiredPermissions={['platform:admin']} />}
        />
        <Route
          path="/platform/session-logs"
          element={<AuthGuard element={<SessionLogsPage />} requiredPermissions={['platform:admin']} />}
        />
        <Route
          path="/platform/chat-monitor"
          element={<AuthGuard element={<ChatMonitorPage />} requiredPermissions={['platform:admin']} />}
        />
        <Route
          path="/platform/pricing"
          element={<AuthGuard element={<PricingPage />} requiredPermissions={['platform:admin']} />}
        />
        <Route
          path="/platform/tool-responses"
          element={<AuthGuard element={<ToolErrorsPage />} requiredPermissions={['platform:admin']} />}
        />
        <Route path="/platform/tool-errors" element={<Navigate to="/platform/tool-responses" replace />} />
        <Route
          path="/platform/ai-billing"
          element={<AuthGuard element={<AiBillingPage />} requiredPermissions={['platform:admin']} />}
        />
        <Route
          path="/platform/usage"
          element={<AuthGuard element={<UsagePage />} requiredPermissions={['platform:admin']} />}
        />
        <Route
          path="/platform/notifications"
          element={<AuthGuard element={<NotificationsAdminPage />} requiredPermissions={['platform:admin']} />}
        />
        <Route
          path="/platform/queues"
          element={<AuthGuard element={<QueueManagementPage />} requiredPermissions={['platform:admin']} />}
        />
        <Route
          path="/platform/scheduler-events"
          element={<AuthGuard element={<SchedulerEventsPage />} requiredPermissions={['platform:admin']} />}
        />
        <Route
          path="/platform/workflows"
          element={<AuthGuard element={<WorkflowsPage />} requiredPermissions={['platform:admin']} />}
        />
        <Route
          path="/platform/workflows/new"
          element={<AuthGuard element={<WorkflowCreateWizardPage />} requiredPermissions={['platform:admin']} />}
        />
        <Route
          path="/platform/workflows/:definitionId/:version/edit"
          element={<AuthGuard element={<WorkflowEditorPage />} requiredPermissions={['platform:admin']} />}
        />
        <Route
          path="/platform/workflows/:definitionId/:version/flowchart"
          element={<AuthGuard element={<WorkflowFlowchartPage />} requiredPermissions={['platform:admin']} />}
        />
        <Route
          path="/platform/workflows/:definitionId/:version/governance"
          element={<AuthGuard element={<WorkflowGovernancePage />} requiredPermissions={['platform:admin']} />}
        />
        <Route
          path="/platform/workflows/:definitionId/:version"
          element={<AuthGuard element={<WorkflowDefinitionPage />} requiredPermissions={['platform:admin']} />}
        />
        {/* /notifications removed — all notifications are in the workspace NotificationsPanel */}
      </Route>

      {/* Default */}
      <Route path="/" element={<AuthGuard element={<RootRedirect />} />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
    </ErrorBoundary>
  );
};

export default App;
